import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { TabsList } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"

// Wraps a TabsList in a horizontally scrollable container with chevron
// buttons that appear only when there's room to scroll. The chevrons sit
// over the scroller's edges with a thick ring matching the page bg so they
// visually mask any partially-clipped tab behind them.
export function ScrollableTabsList({ className, children, variant, ...props }) {
  const scrollerRef = React.useRef(null)
  const [canScrollLeft, setCanScrollLeft] = React.useState(false)
  const [canScrollRight, setCanScrollRight] = React.useState(false)

  const updateScrollState = React.useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  React.useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener("scroll", updateScrollState, { passive: true })
    const ro = new ResizeObserver(updateScrollState)
    ro.observe(el)
    Array.from(el.children).forEach((c) => ro.observe(c))
    return () => {
      el.removeEventListener("scroll", updateScrollState)
      ro.disconnect()
    }
  }, [updateScrollState])

  const scrollBy = (delta) => {
    scrollerRef.current?.scrollBy({ left: delta, behavior: "smooth" })
  }

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        className="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <TabsList variant={variant} className={cn("flex-nowrap bg-border", className)} {...props}>
          {children}
        </TabsList>
      </div>
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scrollBy(-200)}
          aria-label="Scroll tabs left"
          className="absolute left-0 top-[calc(50%-0.25rem)] z-10 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-foreground text-background shadow-lg transition-colors hover:bg-foreground/85"
        >
          <ChevronLeft className="size-5" />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scrollBy(200)}
          aria-label="Scroll tabs right"
          className="absolute right-0 top-[calc(50%-0.25rem)] z-10 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-foreground text-background shadow-lg transition-colors hover:bg-foreground/85"
        >
          <ChevronRight className="size-5" />
        </button>
      )}
    </div>
  )
}
